import { useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { num, pct, tone } from "@/lib/api";

const SECTORS = ["All", "Index", "Bank", "IT", "Auto", "Energy", "FMCG", "Infra"];

const Spark = ({ data, up }) => {
  if (!data?.length) return null;
  const min = Math.min(...data), max = Math.max(...data), r = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 60},${18 - ((v - min) / r) * 16}`).join(" ");
  return (
    <svg width="60" height="20" className="shrink-0">
      <polyline points={pts} fill="none" stroke={up ? "#00E676" : "#FF3B30"} strokeWidth="1.2" />
    </svg>
  );
};

const Row = ({ q, active, onSelect }) => {
  const prev = useRef(q.price);
  const dir = q.price > prev.current ? "flash-up" : q.price < prev.current ? "flash-down" : "";
  prev.current = q.price;
  return (
    <button
      data-testid={`watchlist-symbol-${q.symbol.toLowerCase()}`}
      onClick={() => onSelect(q.symbol)}
      className={`w-full flex items-center gap-2 px-3 py-2 border-b border-[#102A4A] text-left transition-colors hover:bg-[#102A4A] ${active ? "bg-[#102A4A] border-l-2 border-l-[#22A34A]" : "border-l-2 border-l-transparent"}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-100 truncate">{q.symbol}</div>
        <div className="text-[10px] text-slate-500 truncate">{q.name}</div>
      </div>
      <Spark data={q.spark} up={q.change >= 0} />
      <div className="text-right w-[84px]">
        <div key={q.price} className={`font-mono text-xs text-slate-100 px-1 rounded-sm ${dir}`} data-testid={`watchlist-price-${q.symbol.toLowerCase()}`}>
          {num(q.price)}
        </div>
        <div className={`font-mono text-[10px] ${tone(q.change)}`}>{pct(q.change_pct)}</div>
      </div>
    </button>
  );
};

export const Watchlist = ({ quotes, symbol, onSelect }) => {
  const [sector, setSector] = useState("All");
  const [q, setQ] = useState("");
  const list = useMemo(
    () => quotes.filter((x) => (sector === "All" || x.sector === sector) && (x.symbol + x.name).toLowerCase().includes(q.toLowerCase())),
    [quotes, sector, q],
  );
  return (
    <aside className="w-full md:w-72 border-r border-[#163056] bg-[#0B1F3A] flex flex-col shrink-0 md:h-[calc(100vh-3.5rem)]" data-testid="watchlist">
      <div className="p-3 border-b border-[#163056] space-y-2">
        <div className="widget-title">NSE Watchlist</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-500" />
          <input data-testid="watchlist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search symbol" className="field w-full pl-7" />
        </div>
        <div className="flex flex-wrap gap-1">
          {SECTORS.map((s) => (
            <button key={s} data-testid={`sector-filter-${s.toLowerCase()}`} onClick={() => setSector(s)}
              className={`px-2 py-0.5 text-[10px] font-mono rounded-sm border transition-colors ${sector === s ? "border-[#22A34A] text-[#22A34A]" : "border-[#163056] text-slate-500 hover:text-slate-200"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-64 md:max-h-none">
        {list.map((x) => <Row key={x.symbol} q={x} active={x.symbol === symbol} onSelect={onSelect} />)}
        {!list.length && <div className="p-4 text-xs text-slate-500">No symbols</div>}
      </div>
    </aside>
  );
};
